import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { GoogleAdsConnectBanner } from '~/features/catalog-operations/components/google-ads-connect-banner';
import {
  useBatchSearchImpactSummary,
  useRefreshProductSearchImpact,
  useSetReviewItemDismissed,
} from '~/features/catalog-operations/hooks/use-product-search-impact';
import type { ProductSearchImpact } from '~/features/catalog-operations/types/search-impact.types';

import { ChangeLedger } from './change-ledger';
import { DescriptorCoverageMatrix } from './descriptor-coverage-matrix';
import { NeedsReviewPanel } from './needs-review-panel';
import { SearchImpactBatchStrip } from './search-impact-batch-strip';
import { SearchImpactStatCards } from './search-impact-stat-cards';

interface Props {
  productId: string;
  /** Enrichment job being viewed — pins the producer and feeds the batch strip. */
  batchId: string;
  data: ProductSearchImpact | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Switches to the Product Detail tab, where the source data lives. */
  onOpenSourceData: () => void;
}

/** Card-shaped message for the not-calculated / failed states, with an optional action. */
function Notice({
  title,
  body,
  action,
  testId,
}: {
  title: string;
  body: string;
  action?: { label: string; onClick: () => void; isLoading: boolean };
  /** Root `data-testid`; title, message and action derive from it. */
  testId: string;
}) {
  return (
    <div
      className="flex flex-col items-center gap-3 overflow-hidden rounded-2xl border bg-white px-6 py-20 text-center shadow-sm"
      data-testid={testId}
    >
      <p
        className="text-foreground text-sm leading-5 font-semibold"
        data-testid={`${testId}-title`}
      >
        {title}
      </p>
      <p
        className="text-muted-foreground max-w-prose text-sm leading-5"
        data-testid={`${testId}-message`}
      >
        {body}
      </p>
      {action && (
        <Button
          data-testid={`${testId}-button`}
          type="button"
          className="mt-2 cursor-pointer"
          isLoading={action.isLoading}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}

/**
 * Search Impact tab (layout A): stat cards → batch strip → descriptor coverage →
 * change ledger → needs review. Replaces the separate Gap Analysis and Search
 * Descriptors tabs when the `search_impact_ui` gate is on.
 */
export function SearchImpactTab({
  productId,
  batchId,
  data,
  isLoading,
  isError,
  onOpenSourceData,
}: Props) {
  const jobId = batchId || undefined;
  const refresh = useRefreshProductSearchImpact(productId, jobId);
  const dismiss = useSetReviewItemDismissed(productId, jobId);
  const { data: batchSummary } = useBatchSearchImpactSummary(jobId, data?.status === 'COMPLETED');
  const calculate = {
    onClick: () => {
      refresh.mutate();
    },
    isLoading: refresh.isPending,
  };

  return (
    <div className="flex flex-col gap-5" data-testid="search-impact-panel">
      {/* Volume / competition need a Google Ads link. */}
      <GoogleAdsConnectBanner feature="Search Impact" />
      {isLoading ? (
        <>
          <Skeleton className="h-32 w-full rounded-2xl" data-testid="search-impact-loading" />
          <Skeleton className="h-80 w-full rounded-2xl" />
          <Skeleton className="h-80 w-full rounded-2xl" />
        </>
      ) : isError || !data ? (
        // Distinct from "not calculated" — a failed request must not read as "no data".
        <Notice
          testId="search-impact-error"
          title="Search impact could not be loaded."
          body="Something went wrong fetching this product's search impact. Try reloading."
        />
      ) : data.status === 'NOT_COMPUTED' ? (
        <Notice
          testId="search-impact-not-computed-empty"
          title="Search impact hasn't been calculated for this product yet."
          body="It's calculated after enrichment. You can calculate it now — it takes a few seconds."
          action={{ label: 'Calculate', ...calculate }}
        />
      ) : data.status === 'FAILED' ? (
        <Notice
          testId="search-impact-failed-error"
          title="Search impact couldn't be calculated."
          body={data.error ?? 'The last calculation failed.'}
          action={{ label: 'Try again', ...calculate }}
        />
      ) : (
        <>
          <SearchImpactStatCards
            summary={data.summary}
            openReviewCount={data.review.filter((item) => !item.dismissed).length}
            withheldCount={data.descriptors.filter((d) => d.withheldReason !== null).length}
          />
          {batchSummary && jobId && (
            <SearchImpactBatchStrip batchId={jobId} summary={batchSummary} />
          )}
          <DescriptorCoverageMatrix data={data} />
          <ChangeLedger ledger={data.ledger} summary={data.summary} />
          <NeedsReviewPanel
            review={data.review}
            cleared={data.cleared}
            pendingItemKey={dismiss.isPending ? dismiss.variables.itemKey : null}
            onSetDismissed={(itemKey, dismissed) => {
              dismiss.mutate({ itemKey, dismissed });
            }}
            onOpenSourceData={onOpenSourceData}
          />
        </>
      )}
    </div>
  );
}
